/**
 * Mission routes on the map: what is on the aircraft, what is being planned,
 * and what a machine has asked a human to approve.
 *
 * Three sources, not one with a style expression. The uploaded mission, the
 * draft and the proposal are different claims about the world, and a reviewer
 * of this file should be able to see that the solid line comes from a vehicle
 * acknowledgment, the dashed one comes from somebody's intent, and the dotted
 * magenta one comes from a model and has not been agreed to by anybody. One
 * source with a `kind` property would put that distinction inside a paint
 * expression, which is exactly where the predecessor lost it: it drew a single
 * "mission" layer fed by whichever list happened to be in the planning hook, so
 * a failed upload still showed a route on the map.
 *
 * The proposed layer's colour is deliberately NOT the per-drone track colour.
 * A proposal that looks like the fleet's own routes is a proposal an operator
 * can mistake for something already agreed, which is the one misreading the
 * review gate exists to make impossible.
 *
 * Redraws are driven by `subscribeToMissionGeometry`, which fires on route
 * changes only. Renaming a plan does not touch the map.
 */

import { geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import { trackColorFor } from '@/map/icons';
import { subscribeToProposedMission } from '@/stores/copilotStore';
import { subscribeToRoster, useDroneStore } from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';
import {
  isPositional,
  subscribeToMissionGeometry,
  type MissionMap,
} from '@/stores/missionStore';
import type { DroneId, Waypoint } from '@/types/domain';
import { MavCmd } from '@/types/domain';
import { useEffect } from 'react';

export const UPLOADED_SOURCE_ID = 'mission-uploaded';
export const DRAFT_SOURCE_ID = 'mission-draft';
export const PROPOSED_SOURCE_ID = 'mission-proposed';

const UPLOADED_LINE_LAYER = 'mission-uploaded-line';
const UPLOADED_POINT_LAYER = 'mission-uploaded-point';
const UPLOADED_LABEL_LAYER = 'mission-uploaded-label';
const DRAFT_LINE_LAYER = 'mission-draft-line';
// Exported: the planning screen hit-tests this layer to drag a waypoint.
export const DRAFT_POINT_LAYER = 'mission-draft-point';
const DRAFT_LABEL_LAYER = 'mission-draft-label';
const PROPOSED_LINE_LAYER = 'mission-proposed-line';
const PROPOSED_POINT_LAYER = 'mission-proposed-point';
const PROPOSED_LABEL_LAYER = 'mission-proposed-label';

/** One colour for the whole proposal, in a hue nothing else on the map uses. */
export const PROPOSED_COLOR = '#e35cff';

const EMPTY = { type: 'FeatureCollection', features: [] as unknown[] };

/** Short names for the commands a route can contain, for the waypoint labels. */
const COMMAND_LABEL: Record<number, string> = {
  [MavCmd.NavWaypoint]: 'WP',
  [MavCmd.NavLoiterUnlim]: 'LOITER',
  [MavCmd.NavLoiterTurns]: 'TURNS',
  [MavCmd.NavLoiterTime]: 'HOLD',
  [MavCmd.NavReturnToLaunch]: 'RTL',
  [MavCmd.NavLand]: 'LAND',
  [MavCmd.NavTakeoff]: 'TAKEOFF',
  [MavCmd.DoChangeSpeed]: 'SPEED',
  [MavCmd.DoSetRoi]: 'ROI',
};

export function commandLabel(command: number): string {
  return COMMAND_LABEL[command] ?? `CMD ${command}`;
}

/** A colour per command type, so a route reads at a glance: takeoff, land, RTL
 *  and ROI stand apart from plain waypoints. Used for the point fill on the map
 *  and the type badge in the plan panel, so the two always agree. The per-drone
 *  track colour stays on the stroke, so command type and aircraft identity are
 *  both legible on one dot. */
const COMMAND_COLOR: Record<number, string> = {
  [MavCmd.NavWaypoint]: '#4ea1ff',
  [MavCmd.NavTakeoff]: '#4ec9a3',
  [MavCmd.NavLand]: '#f0563f',
  [MavCmd.NavReturnToLaunch]: '#f2b23a',
  [MavCmd.NavLoiterUnlim]: '#b98cff',
  [MavCmd.NavLoiterTurns]: '#b98cff',
  [MavCmd.NavLoiterTime]: '#b98cff',
  [MavCmd.DoSetRoi]: '#ff8bd0',
  [MavCmd.DoChangeSpeed]: '#8a94a6',
};
const COMMAND_COLOR_FALLBACK = '#4ea1ff';

export function commandColor(command: number): string {
  return COMMAND_COLOR[command] ?? COMMAND_COLOR_FALLBACK;
}

/** A data-driven MapLibre `match` on the numeric command carried per point. */
function commandColorExpression(): unknown {
  const pairs: unknown[] = [];
  for (const [command, color] of Object.entries(COMMAND_COLOR)) {
    pairs.push(Number(command), color);
  }
  return ['match', ['get', 'cmd'], ...pairs, COMMAND_COLOR_FALLBACK];
}

function routeFeatures(missions: MissionMap, selected: DroneId | null): unknown[] {
  const features: unknown[] = [];

  for (const [droneId, waypoints] of Object.entries(missions)) {
    if (!waypoints || waypoints.length === 0) continue;
    if (selected !== null && droneId !== selected) continue;

    const color = trackColorFor(droneId);
    const positional: Waypoint[] = waypoints.filter(isPositional);
    const line = positional.map((waypoint) => [waypoint.lon, waypoint.lat]);

    if (line.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { droneId, color },
        geometry: { type: 'LineString', coordinates: line },
      });
    }

    waypoints.forEach((waypoint, index) => {
      if (!isPositional(waypoint)) return;
      features.push({
        type: 'Feature',
        properties: {
          droneId,
          color,
          seq: index + 1,
          label: `${index + 1}`,
          command: commandLabel(waypoint.command),
          cmd: waypoint.command,
          altM: Math.round(waypoint.alt),
        },
        geometry: { type: 'Point', coordinates: [waypoint.lon, waypoint.lat] },
      });
    });
  }

  return features;
}

export interface MissionLayerOptions {
  /** Show only this drone's route. Null shows every drone with a mission. */
  onlyDroneId?: DroneId | null;
  /** Hide the draft line. Used by the command screen, which flies what is loaded. */
  showDraft?: boolean;
}

export interface MissionLayer {
  destroy: () => void;
  /** Redraws applied. Non-zero is the proof this layer is wired to the store. */
  stats: () => { redraws: number };
  setOptions: (options: MissionLayerOptions) => void;
}

export function createMissionLayer(
  map: MapLike,
  initialOptions: MissionLayerOptions = {},
): MissionLayer {
  let options: MissionLayerOptions = { showDraft: true, onlyDroneId: null, ...initialOptions };
  let redraws = 0;
  let latest: { draft: MissionMap; uploaded: MissionMap; proposed: MissionMap } = {
    draft: {},
    uploaded: {},
    proposed: {},
  };

  for (const sourceId of [UPLOADED_SOURCE_ID, DRAFT_SOURCE_ID, PROPOSED_SOURCE_ID]) {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: EMPTY });
    }
  }

  function addLineLayer(id: string, source: string, dashed: boolean): void {
    if (map.getLayer(id)) return;
    map.addLayer({
      id,
      type: 'line',
      source,
      filter: ['==', ['geometry-type'], 'LineString'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': dashed ? 2 : 3,
        'line-opacity': dashed ? 0.7 : 0.95,
        ...(dashed ? { 'line-dasharray': [2, 2] } : {}),
      },
    });
  }

  function addPointLayer(id: string, source: string, draft: boolean): void {
    if (map.getLayer(id)) return;
    map.addLayer({
      id,
      type: 'circle',
      source,
      filter: ['==', ['geometry-type'], 'Point'],
      paint: {
        'circle-radius': draft ? 6 : 7,
        // Fill is the command type; the per-drone track colour stays on the
        // stroke. Draft points are dimmer so an uploaded route still reads as
        // the one that is actually on the aircraft.
        'circle-color': commandColorExpression(),
        'circle-opacity': draft ? 0.55 : 1,
        'circle-stroke-color': ['get', 'color'],
        'circle-stroke-width': 2,
      },
    });
  }

  function addLabelLayer(id: string, source: string): void {
    if (map.getLayer(id)) return;
    map.addLayer({
      id,
      type: 'symbol',
      source,
      filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-offset': [0, -1.3],
      },
      paint: {
        'text-color': '#ffffff',
        'text-halo-color': 'rgba(0,0,0,0.85)',
        'text-halo-width': 1.2,
      },
    });
  }

  /**
   * The proposal, drawn so it cannot be mistaken for an agreed route: one
   * fixed magenta rather than the aircraft's own colour, a dotted line rather
   * than solid or dashed, and hollow points rather than filled command dots.
   * Added last, so it sits above both — a proposal hidden under the draft it
   * is meant to replace is a proposal nobody reviews.
   */
  function addProposedLayers(): void {
    if (!map.getLayer(PROPOSED_LINE_LAYER)) {
      map.addLayer({
        id: PROPOSED_LINE_LAYER,
        type: 'line',
        source: PROPOSED_SOURCE_ID,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': PROPOSED_COLOR,
          'line-width': 2.5,
          'line-opacity': 0.9,
          'line-dasharray': [1, 1.6],
        },
      });
    }
    if (!map.getLayer(PROPOSED_POINT_LAYER)) {
      map.addLayer({
        id: PROPOSED_POINT_LAYER,
        type: 'circle',
        source: PROPOSED_SOURCE_ID,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5.5,
          'circle-color': 'rgba(0,0,0,0.55)',
          'circle-stroke-color': PROPOSED_COLOR,
          'circle-stroke-width': 2,
        },
      });
    }
    addLabelLayer(PROPOSED_LABEL_LAYER, PROPOSED_SOURCE_ID);
  }

  addLineLayer(DRAFT_LINE_LAYER, DRAFT_SOURCE_ID, true);
  addLineLayer(UPLOADED_LINE_LAYER, UPLOADED_SOURCE_ID, false);
  addPointLayer(DRAFT_POINT_LAYER, DRAFT_SOURCE_ID, true);
  addPointLayer(UPLOADED_POINT_LAYER, UPLOADED_SOURCE_ID, false);
  addLabelLayer(DRAFT_LABEL_LAYER, DRAFT_SOURCE_ID);
  addLabelLayer(UPLOADED_LABEL_LAYER, UPLOADED_SOURCE_ID);
  addProposedLayers();

  // A route belongs to an aircraft. When that aircraft is gone — a sim
  // despawned, a plan closed — its waypoints are orphans that render forever
  // with no way to select or delete them, because every editing control targets
  // a drone that no longer exists. Draw a route only for an aircraft that is
  // actually on the roster.
  function present(missions: MissionMap): MissionMap {
    const roster = new Set(useDroneStore.getState().ids);
    const out: MissionMap = {};
    for (const [droneId, waypoints] of Object.entries(missions)) {
      if (roster.has(droneId as DroneId)) out[droneId] = waypoints;
    }
    return out;
  }

  function redraw(): void {
    const only = options.onlyDroneId ?? null;
    const uploadedSource = geoJsonSource(map, UPLOADED_SOURCE_ID);
    const draftSource = geoJsonSource(map, DRAFT_SOURCE_ID);
    const proposedSource = geoJsonSource(map, PROPOSED_SOURCE_ID);
    if (!uploadedSource || !draftSource || !proposedSource) return;

    const uploaded = present(latest.uploaded);
    const draft = present(latest.draft);
    // Same roster rule as the other two: a proposal naming an aircraft that is
    // not on the fleet draws nothing, rather than an unselectable orphan route.
    const proposed = present(latest.proposed);

    uploadedSource.setData({
      type: 'FeatureCollection',
      features: routeFeatures(uploaded, only),
    });

    // A draft identical to the uploaded mission is not drawn twice. Two lines
    // exactly on top of each other read as a thicker line, which is the one
    // appearance that should mean "these differ".
    const draftToDraw: MissionMap = {};
    if (options.showDraft !== false) {
      for (const [droneId, waypoints] of Object.entries(draft)) {
        if (!waypoints || !waypoints.length) continue;
        if (sameRoute(waypoints, uploaded[droneId])) continue;
        draftToDraw[droneId] = waypoints;
      }
    }
    draftSource.setData({
      type: 'FeatureCollection',
      features: routeFeatures(draftToDraw, only),
    });

    // A proposal is never suppressed for matching the draft. The whole point of
    // drawing it is to show what would change, including "nothing".
    proposedSource.setData({
      type: 'FeatureCollection',
      features: routeFeatures(proposed, only),
    });

    redraws += 1;
  }

  const unsubscribe = subscribeToMissionGeometry(({ draft, uploaded }) => {
    latest = { ...latest, draft, uploaded };
    redraw();
  });
  const unsubscribeProposed = subscribeToProposedMission((proposed) => {
    latest = { ...latest, proposed };
    redraw();
  });
  // Redraw when the roster changes so a route whose aircraft just left the
  // fleet stops being drawn immediately, rather than lingering as an orphan.
  const unsubscribeRoster = subscribeToRoster(() => redraw());

  return {
    destroy: () => {
      unsubscribe();
      unsubscribeProposed();
      unsubscribeRoster();
      for (const id of [
        PROPOSED_LABEL_LAYER,
        PROPOSED_POINT_LAYER,
        PROPOSED_LINE_LAYER,
        UPLOADED_LABEL_LAYER,
        UPLOADED_POINT_LAYER,
        UPLOADED_LINE_LAYER,
        DRAFT_LABEL_LAYER,
        DRAFT_POINT_LAYER,
        DRAFT_LINE_LAYER,
      ]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      for (const id of [UPLOADED_SOURCE_ID, DRAFT_SOURCE_ID, PROPOSED_SOURCE_ID]) {
        if (map.getSource(id)) map.removeSource(id);
      }
    },
    stats: () => ({ redraws }),
    setOptions: (next) => {
      options = { ...options, ...next };
      redraw();
    },
  };
}

/** Coordinate and command equality. Two routes that fly the same are the same. */
export function sameRoute(a: Waypoint[], b: Waypoint[] | undefined): boolean {
  if (!b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (!left || !right) return false;
    if (
      left.lat !== right.lat ||
      left.lon !== right.lon ||
      left.alt !== right.alt ||
      left.command !== right.command ||
      left.frame !== right.frame
    ) {
      return false;
    }
  }
  return true;
}

export function useMissionLayer(options: MissionLayerOptions = {}): void {
  const controller = useMapController();
  const onlyDroneId = options.onlyDroneId ?? null;
  const showDraft = options.showDraft !== false;
  const uploadedOn = useMapStore((state) => state.layers.routesUploaded);
  const draftOn = useMapStore((state) => state.layers.routesDraft);

  useEffect(() => {
    let layer: MissionLayer | null = null;
    const release = controller.ready((map) => {
      layer = createMissionLayer(map, { onlyDroneId, showDraft });
      return () => {
        layer?.destroy();
        layer = null;
      };
    });
    return release;
  }, [controller, onlyDroneId, showDraft]);

  // Visibility rather than a rebuild, and in its own effect: tearing the layer
  // down on every toggle would re-fetch and re-draw every route, and the
  // uploaded and draft switches are independent of each other.
  //
  // The proposed-route layers follow the draft switch. A proposal is a draft
  // somebody has not accepted yet, so an operator who has hidden drafts to read
  // the map has not asked to keep seeing the AI's suggestions on top of it.
  useEffect(
    () =>
      controller.ready((map) => {
        const apply = (): void => {
          for (const [id, on] of [
            [UPLOADED_LINE_LAYER, uploadedOn],
            [UPLOADED_POINT_LAYER, uploadedOn],
            [UPLOADED_LABEL_LAYER, uploadedOn],
            [DRAFT_LINE_LAYER, draftOn],
            [DRAFT_POINT_LAYER, draftOn],
            [DRAFT_LABEL_LAYER, draftOn],
            [PROPOSED_LINE_LAYER, draftOn],
            [PROPOSED_POINT_LAYER, draftOn],
            [PROPOSED_LABEL_LAYER, draftOn],
          ] as [string, boolean][]) {
            if (map.getLayer(id)) {
              map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
            }
          }
        };
        apply();
        // The mission layer adds its layers when the style settles, which can be
        // after this runs; without the second pass a switch set before the map
        // was ready would be silently ignored.
        map.on('idle', apply);
        return () => {
          map.off('idle', apply);
        };
      }),
    [controller, uploadedOn, draftOn],
  );
}
